import { type FC } from "react";
import { useFormContext } from "react-hook-form";
import { RadioButton } from "@root/shared/components/RadioButton";
import { SelectBox } from "@root/shared/components/SelectBox";
import { TextArea } from "@root/shared/components/TextArea";
import { Table, Td, Th, Tr } from "@root/shared/components/Table";
import { Checkbox } from "@root/shared/components/Checkbox";
import { Label } from "@root/shared/components/Label";
import { type InputNotAdoptedDetailViewProps } from "@clientFrontend/features/entry/components/NotAdopted/type";
import { Notice } from "./uiParts/Notice";
import * as styles from "./styles.css";

export const PcView: FC<InputNotAdoptedDetailViewProps> = ({
  completedInterview,
  isPayPerJobEntryFeeType,
  handleRadioButtonChange,
  isChecked,
  handleChangeCheckbox,
  notAdoptedDescriptionTextLength,
  handleTextAreaChange,
  recruitmentFeePath,
  selectBoxOptions,
  selectedReason,
}) => {
  const {
    register,
    formState: { errors },
  } = useFormContext();

  return (
    <Table>
      <Tr>
        <Th width="168px" className={styles.formItem}>
          面接・見学
          <Label theme="deepPink" className={styles.formLabel}>
            必須
          </Label>
        </Th>
        <Td>
          <div className={styles.formRadioButtonRow}>
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label className={styles.formRadioButton}>
              <RadioButton
                {...register("completed_interview", {
                  required: "選択してください。",
                })}
                checked={completedInterview === "true"}
                hasError={!!errors.completed_interview}
                onChange={() => handleRadioButtonChange("true")}
                value="true"
              />
              <span className={styles.formRadioLabel}>実施した</span>
            </label>
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label className={styles.formRadioButton}>
              <RadioButton
                {...register("completed_interview", {
                  required: "選択してください。",
                })}
                checked={completedInterview === "false"}
                hasError={!!errors.completed_interview}
                onChange={() => handleRadioButtonChange("false")}
                value="false"
              />
              <span className={styles.formRadioLabel}>実施していない</span>
            </label>
          </div>
          {errors.completed_interview && (
            <p className={styles.errorMessage}>{errors.completed_interview.message}</p>
          )}
        </Td>
      </Tr>
      <Tr>
        <Th width="168px" className={styles.formItem}>
          不採用理由
          <Label theme="deepPink" className={styles.formLabel}>
            必須
          </Label>
        </Th>
        <Td>
          <SelectBox
            {...register("not_adopted_reason", {
              required: "選択してください。",
            })}
            hasError={!!errors.not_adopted_reason}
            selected={!!selectedReason}
            options={selectBoxOptions}
            placeholder="不採用理由をご選択ください"
            className={styles.formSelectBox}
          />
          {errors.not_adopted_reason && (
            <p className={styles.errorMessage}>{errors.not_adopted_reason.message}</p>
          )}
        </Td>
      </Tr>
      <Tr>
        <Th width="168px" className={styles.formItem}>
          不採用理由の
          <br />
          詳細
          <Label theme="gray" className={styles.formLabel}>
            任意
          </Label>
        </Th>
        <Td>
          <p>不採用にされた理由の詳細をお知らせください。</p>
          <TextArea
            {...register("not_adopted_description", {
              maxLength: {
                value: 100,
                message: "100文字以内で入力してください。",
              },
            })}
            hasError={!!errors.not_adopted_description}
            placeholder={"何か問題のある方でしたか？\n例）面接に来なかった。連絡が付かなくなった。"}
            onChange={(e) => handleTextAreaChange(e)}
            className={styles.formTextArea}
          />
          <p>
            <span
              className={notAdoptedDescriptionTextLength > 100 ? styles.formTextLengthError : ""}
            >
              {notAdoptedDescriptionTextLength}
            </span>
            /100文字以内
          </p>
          {errors.not_adopted_description && (
            <p className={styles.errorMessage}>{errors.not_adopted_description.message}</p>
          )}
        </Td>
      </Tr>
      <Tr>
        <Th width="168px" className={styles.formItem}>
          注意事項
          <Label theme="deepPink" className={styles.formLabel}>
            必須
          </Label>
        </Th>
        <Td>
          <Notice
            isPayPerJobEntryFeeType={isPayPerJobEntryFeeType}
            recruitmentFeePath={recruitmentFeePath}
          />
          <div className={styles.formNoteCheckBox}>
            {/* このパラメータはサーバー側では使用されません。チェック項目としてフロント側のバリデーションチェックでのみ使用します。 */}
            <Checkbox
              {...register("note", {
                required: "選択してください。",
              })}
              id="is_confirmed"
              checked={isChecked}
              hasError={!!errors.note}
              onChange={handleChangeCheckbox}
            />
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label htmlFor="is_confirmed" className={styles.formNoteCheckBoxLabel}>
              注意事項に同意する
            </label>
          </div>
          {errors.note && <p className={styles.errorMessage}>{errors.note.message}</p>}
        </Td>
      </Tr>
    </Table>
  );
};
